import API from 'api';
import { normalizeSavedSearch } from 'api/normalize';
import { LOAD_HEADER_SUCCESS, LoadHeaderSuccessAction } from 'modules/loaders';
import { getMyId, getSavedSearchById, getSavedSearches } from 'selectors';
import {
  SavedSearchModel,
  SavedSearchResponse,
  StandardThunkAction,
} from 'types';
import { Action, actionCreator } from 'util/actions';
import { calculatePos } from 'util/position';
import { createReducer } from 'util/reducers';

export interface SavedSearchUpdate {
  id: string;
  position: number;
}

export interface SavedSearchState {
  [savedSearchId: string]: SavedSearchModel;
}

const ADD_SAVED_SEARCH_SUCCESS = Symbol('ADD_SAVED_SEARCH_SUCCESS');
const ADD_SAVED_SEARCH_FAILURE = Symbol('ADD_SAVED_SEARCH_FAILURE');

const REMOVE_SAVED_SEARCH_SUCCESS = Symbol('REMOVE_SAVED_SEARCH_SUCCESS');
const REMOVE_SAVED_SEARCH_FAILURE = Symbol('REMOVE_SAVED_SEARCH_FAILURE');

const UPDATE_POS_REQUEST = Symbol('UPDATE_POS_REQUEST');
const UPDATE_POS_SUCCESS = Symbol('UPDATE_POS_SUCCESS');
const UPDATE_POS_ERROR = Symbol('UPDATE_POS_ERROR');

type AddSavedSearchAction = Action<
  typeof ADD_SAVED_SEARCH_SUCCESS,
  SavedSearchResponse
>;

type RemoveSavedSearchAction = Action<
  typeof REMOVE_SAVED_SEARCH_SUCCESS,
  { id: string }
>;

type UpdatePosRequestAction = Action<
  typeof UPDATE_POS_REQUEST,
  SavedSearchResponse
>;

type UpdatePosSuccessAction = Action<
  typeof UPDATE_POS_SUCCESS,
  SavedSearchResponse
>;

type UpdatePosErrorAction = Action<typeof UPDATE_POS_ERROR, SavedSearchModel>;

// Actions Creators
export const addSavedSearchAction = actionCreator<AddSavedSearchAction>(
  ADD_SAVED_SEARCH_SUCCESS,
);

export const removeSavedSearchAction = actionCreator<RemoveSavedSearchAction>(
  REMOVE_SAVED_SEARCH_SUCCESS,
);

export const savedSearchPosUpdateActionStart = actionCreator<
  UpdatePosRequestAction
>(UPDATE_POS_REQUEST);

export const savedSearchPosUpdateActionSuccess = actionCreator<
  UpdatePosSuccessAction
>(UPDATE_POS_SUCCESS);

export const savedSearchPosUpdateActionError = actionCreator<
  UpdatePosErrorAction
>(UPDATE_POS_ERROR);

//Helper
const upsertSavedSearch = (
  state: SavedSearchState,
  savedSearch: SavedSearchResponse,
) => ({
  ...state,
  [savedSearch.id]: {
    ...(state[savedSearch.id] || {}),
    ...normalizeSavedSearch(savedSearch),
  },
});

//Actions
export const addSavedSearch = (
  savedSearch: SavedSearchModel,
): StandardThunkAction => {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const idMember = getMyId(state);

      const returnedSavedSearch = await API.client.rest.post<
        SavedSearchResponse
      >(`/member/${idMember}/savedSearches`, {
        body: savedSearch,
      });

      dispatch(addSavedSearchAction(returnedSavedSearch));
    } catch (err) {
      dispatch(actionCreator(ADD_SAVED_SEARCH_FAILURE)(err));
    }
  };
};

export const removeSavedSearch = (id: string): StandardThunkAction<void> => {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const idMember = getMyId(state);

      await API.client.rest.del(`members/${idMember}/savedSearches/${id}`);

      dispatch(removeSavedSearchAction({ id }));
    } catch (err) {
      dispatch(actionCreator(REMOVE_SAVED_SEARCH_FAILURE)(err));
    }
  };
};

export const updateSavedSearchPosition = ({
  id,
  position,
}: SavedSearchUpdate): StandardThunkAction<void> => {
  return async (dispatch, getState) => {
    const state = getState();
    const idMember = getMyId(state);
    const savedSearch = getSavedSearchById(state, id);

    if (savedSearch) {
      try {
        const savedSearches = getSavedSearches(state);
        const newPos = calculatePos(position, savedSearches, savedSearch);

        dispatch(
          savedSearchPosUpdateActionStart({ ...savedSearch, pos: newPos }),
        );

        const savedSearchReponse = await API.client.rest.put<SavedSearchModel>(
          `members/${idMember}/savedSearches/${id}`,
          {
            body: {
              pos: newPos,
            },
          },
        );

        dispatch(savedSearchPosUpdateActionSuccess(savedSearchReponse));
      } catch (err) {
        dispatch(savedSearchPosUpdateActionError(savedSearch));
      }
    }
  };
};

// Reducer
const initialState: SavedSearchState = {};

export default createReducer(initialState, {
  [LOAD_HEADER_SUCCESS](state, { payload }: LoadHeaderSuccessAction) {
    const { savedSearches = [] } = payload;

    return savedSearches.reduce(
      (result, search) => upsertSavedSearch(result, search),
      state,
    );
  },
  [ADD_SAVED_SEARCH_SUCCESS](state, { payload }: AddSavedSearchAction) {
    return upsertSavedSearch(state, payload);
  },
  [ADD_SAVED_SEARCH_FAILURE](state, { message }: Error) {
    // TODO: handle this failure

    return state;
  },
  [REMOVE_SAVED_SEARCH_SUCCESS](state, { payload }: RemoveSavedSearchAction) {
    const { [payload.id]: removed, ...rest } = state;

    return rest;
  },
  [REMOVE_SAVED_SEARCH_FAILURE](state, { message }: Error) {
    // TODO: handle this failure

    return state;
  },
  [UPDATE_POS_REQUEST](state, { payload }: UpdatePosRequestAction) {
    return upsertSavedSearch(state, payload);
  },
  [UPDATE_POS_SUCCESS](state, { payload }: UpdatePosSuccessAction) {
    return upsertSavedSearch(state, payload);
  },
  [UPDATE_POS_ERROR](state, { payload }: UpdatePosErrorAction) {
    return upsertSavedSearch(state, payload);
  },
});



// WEBPACK FOOTER //
// ./app/gamma/src/modules/state/models/saved-searches.ts